import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FieldSchema } from '@vendasta/auxiliary-data-components';
import { Observable } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FieldSelectedForEditService as CustomFieldEditorNotificationsService } from '@vendasta/auxiliary-data-components';
import { BulkImportMappingService, fieldType } from './bulk-import-mapping.service';
import { doNotImportOptionId, FieldMappingOption, fieldOptions } from './header-mapping.options';
import { MapChangeDialogComponent } from './map-change-dialog.component';

@Component({
  selector: 'app-bulk-import-mapper-row',
  templateUrl: 'bulk-import-mapping-row.component.html',
  styleUrls: ['./bulk-import-mapping-row.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BulkImportMappingRowComponent implements OnInit {
  @Input() headerRow: string;
  options = fieldOptions;

  oldSelectionId: string = null;
  doNotImportOptionId = doNotImportOptionId;
  searchOptionsControl = new UntypedFormControl();
  filteredOptions$: Observable<FieldMappingOption[]>;
  filteredCustomFieldOptions$: Observable<FieldMappingOption[]>;
  readonly createCustomFieldOption = new CreateCustomField();

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private mappingService: BulkImportMappingService,
    public dialog: MatDialog,
    private readonly customFieldEditorNotificationsService: CustomFieldEditorNotificationsService,
  ) {}

  private get selectedKey(): string {
    return this.searchOptionsControl.value ? this.searchOptionsControl.value.id : null;
  }

  setDoNotImport(): void {
    const oldColumn = this.mappingService.columnThatsMappedToField(this.oldSelectionId);
    this.mappingService.removeFromMap(oldColumn);
    this.setControlValue(doNotImportOptionId);
  }

  onSelectionChange(selection: FieldMappingOption | CreateCustomField): void {
    if ('createCustomField' in selection && selection.createCustomField) {
      this.customFieldEditorNotificationsService.createNewFieldClicked();
      this.customFieldEditorNotificationsService.fieldCreated$$
        .pipe(
          take(1),
          switchMap((newField: FieldSchema) => this.mappingService.getCustomFieldIdByName(newField.fieldName)),
          tap((newFieldId: string) => {
            this.updateMapping(newFieldId, 'custom');
            this.setControlValue(newFieldId);
          }),
        )
        .subscribe();
      return;
    }

    let ft: fieldType = 'standard';
    if ('isCustomFieldOption' in selection && selection.isCustomFieldOption) {
      ft = 'custom';
    }
    if (!('id' in selection)) {
      console.error('did not receive ID for field-mapping');
      return;
    }
    const fieldId: string = selection.id;
    const currentMappedHeader = this.mappingService.columnThatsMappedToField(fieldId);
    if (currentMappedHeader && currentMappedHeader !== this.headerRow) {
      this.openDialog(fieldId, currentMappedHeader, ft);
    } else {
      this.updateMapping(fieldId, ft);
      this.setControlValue(fieldId);
    }
  }

  nameDisplayFn(objectWithAName: { name: string }): string {
    return objectWithAName ? objectWithAName.name : undefined;
  }

  private setControlValue(fieldId: string): void {
    if (fieldId === doNotImportOptionId) {
      this.oldSelectionId = fieldId;
    } else if (fieldId !== this.oldSelectionId) {
      this.oldSelectionId = this.selectedKey ? this.selectedKey : fieldId;
    }
    const matchedStandardOptions: FieldMappingOption[] = this.options.filter((option) => option.id === fieldId);
    if (matchedStandardOptions.length > 0) {
      this.searchOptionsControl.setValue(matchedStandardOptions[0]);
      return;
    }

    this.mappingService.customFieldOptions$
      .pipe(
        take(1),
        tap((options: FieldMappingOption[]) => {
          const matched = options.filter((option) => option.id === fieldId);
          if (matched.length > 0) {
            this.searchOptionsControl.setValue(matched[0]);
          }
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    const searchText$ = this.searchOptionsControl.valueChanges.pipe(
      debounceTime(100),
      filter((value) => value !== null && value !== undefined),
      map((value) => (typeof value === 'string' ? value : value.name)),
    );

    const staticOptions$ = searchText$.pipe(map((value) => this._filterOptions(value)));
    this.filteredOptions$ = staticOptions$;
    this.filteredCustomFieldOptions$ = searchText$.pipe(
      withLatestFrom(this.mappingService.customFieldOptions$),
      map(([searchText, customFieldOptions]) => {
        if (!searchText) {
          return customFieldOptions;
        }
        return customFieldOptions.filter((option) => this.optionMatchesSearch(option.name, searchText));
      }),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

    this.mappingService
      .autoMatch(this.headerRow)
      .pipe(
        take(1),
        tap((autoMatchedKey) => this.setControlValue(autoMatchedKey)),
      )
      .subscribe();
  }

  private optionMatchesSearch(option: string, search: string): boolean {
    return option.toLowerCase().includes(search.toLowerCase());
  }

  openDialog(newFieldChoice: string, currentMappedHeader: string, ft: fieldType): void {
    const dialogRef = this.dialog.open(MapChangeDialogComponent, {
      width: '400px',
      data: {
        headerRow: currentMappedHeader,
        fieldName: newFieldChoice,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.updateMapping(newFieldChoice, ft);
        this.setControlValue(newFieldChoice);
      } else {
        this.setControlValue(this.oldSelectionId);
      }
    });
  }

  private updateMapping(newFieldChoice: string, ft: fieldType): void {
    this.mappingService.addToMap(this.headerRow, newFieldChoice, ft);
  }

  _filterOptions(value: string): FieldMappingOption[] {
    if (!value) {
      return this.options;
    }
    const filterValue = value.toLowerCase();
    return this.options.filter((option) => this.optionMatchesSearch(option.name, filterValue));
  }

  onFocusout(): void {
    if (!this.selectedKey) {
      this.setControlValue(this.oldSelectionId);
    }
  }
}

class CreateCustomField {
  readonly createCustomField: boolean = true;
}
